import React, { useMemo } from "react";
import { Users, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Select } from "../Select";
import { COUNTRIES } from "../../constants/countries";
import { EmptyState } from "../../../../../components/shared/loading/EmptyState";
import { LoadingSpinner } from "../../../../../components/shared/loading/LoadingSpinner";

const MobileTeamsView = ({
  teams,
  filters,
  onFilterChange,
  leagues,
  seasons,
  loading,
  onRefresh,
  pagination,
  onPageChange,
  t
}) => {
  const seasonOptions = useMemo(() => (
    seasons?.map((season) => ({
      value: season,
      label: `${season} ${t('Season')}`
    })) || []
  ), [seasons, t]);

  const paginatedTeams = useMemo(() => {
    if (!pagination) return teams;
    const start = (pagination.currentPage - 1) * pagination.perPage;
    return teams.slice(start, start + pagination.perPage);
  }, [teams, pagination]);

  const handlePageChange = (direction) => {
    if (!pagination) return;
    const nextPage = direction === 'next'
      ? pagination.currentPage + 1
      : pagination.currentPage - 1;
    onPageChange(Math.max(1, Math.min(nextPage, pagination.totalPages)));
  };

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <h2 className="text-lg font-black uppercase tracking-tight text-white flex items-center gap-2">
            {t('Teams')} ({teams.length})
          </h2>
          {pagination && pagination.totalPages > 1 && (
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40">
              {t('Page')} {pagination.currentPage} {t('of')} {pagination.totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <LoadingSpinner message={t('Loading teams...')} />
        ) : teams.length === 0 ? (
          <EmptyState
            title={t('No teams found')}
            description={t('Try adjusting your filters')}
            icon={Users}
          />
        ) : (
          <div className="space-y-3 p-4">
            {paginatedTeams.map((team, index) => (
              <div
                key={team.id || index}
                className="rounded-2xl border border-white/10 bg-black/40 p-4"
              >
                <div className="flex items-center gap-3">
                  {team.logo && (
                    <img
                      src={team.logo}
                      alt={team.name}
                      className="h-12 w-12 rounded-full border border-white/10 bg-black object-cover"
                    />
                  )}
                  <div>
                    <p className="text-base font-semibold text-white">{team.name}</p>
                    {team.country && (
                      <p className="text-xs text-white/50">
                        {team.country.name || team.country}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
                    {t('Team ID')}
                  </p>
                  <p className="text-xl font-black text-white">{team.id}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && teams.length > 0 && pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 border-t border-white/5 px-4 py-3">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={pagination.currentPage === 1}
              className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white/70 transition-all disabled:opacity-40"
            >
              <ChevronLeft size={14} /> {t('Prev')}
            </button>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-white/50">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange('next')}
              disabled={pagination.currentPage === pagination.totalPages}
              className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white/70 transition-all disabled:opacity-40"
            >
              {t('Next')} <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MobileTeamsView;
